import React, { useState } from 'react';
import Modal from 'react-modal';
import theme from '@/data/theme';
import { SeatAPIModel } from '@/apiModels/SeatReservation';
import {
  StyledFilledBorderButton,
  StyledModalHeader,
  StyledSavedButton,
  StyledInputLabel,
  StyledTextAreaField,
  StyledModalTitleWrapper,
  StyledBottomButtonWrapper,
  TableBookingDetailPhoneInputWrapper,
} from '@/components/ui/Modals/EditTableModal/EditTableModal.styled.ts';
import ModalCloseButton from '@/components/ui/ModalCloseButton';
import useUpdateEventSeat from '@/hooks/useUpdateEventSeat.ts';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  StyledModalDetails,
  StyledModalDetailsWrapper,
} from '@/components/ui/Modals/EditBookingModal/EditBookingModal.styled.ts';
import PhoneInput from 'react-phone-number-input';
import LoadingSplash from '@/components/ui/LoadingScreen';

export interface EditBookingPayload {
  remarks?: string;
  id: string;
}

interface IEditBookingProps {
  isOpen: boolean;
  onClose: () => void;
  seatInformation: SeatAPIModel;
  eventId: string;
  seatId: string;
  isLoading: boolean;
  onSuccess: (editingData: EditBookingPayload) => void;
}

const EditBookingModal: React.FunctionComponent<IEditBookingProps> = ({
  isOpen,
  onClose,
  seatInformation,
  eventId,
  seatId,
  onSuccess,
  isLoading,
}) => {
  const queryClient = useQueryClient();
  const [phoneValue, setPhoneValue] = useState(
    seatInformation.reservation_details?.phone_number || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState<EditBookingPayload>({
    id: seatInformation.reservation_details?.id || '',
    remarks: seatInformation.reservation_details?.remarks || '',
  });

  const handleClose = () => {
    setEditFormData({
      id: seatInformation.reservation_details?.id || '',
      remarks: '',
    });
    onClose();
  };

  const { mutate: updateSeatStatus } = useUpdateEventSeat({
    cb: () => {},
    cbSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seat'] });
      onSuccess(editFormData);
      toast.success(
        `You have successfully changed the remarks of ${seatInformation.serial_no} Seat Booking.`,
      );
      setIsSubmitting(false);
      handleClose();
    },
    cbError: () => {
      toast.error('Failed to make changes. Please try again.');
    },
  });

  const handleModalClose = () => {
    onClose();
  };

  const handleEditSeatAction = () => {
    const payload = {
      seat: seatInformation.id,
      reservation_details: {
        id: seatInformation.reservation_details?.id || '',
        fullname: seatInformation.reservation_details?.booked_for || '',
        remarks: editFormData.remarks,
      },
      onClose,
    };
    setIsSubmitting(true);
    updateSeatStatus({
      eventId,
      seatId,
      payload,
    });
  };

  const handlePhoneChange = (newValue: string | undefined) => {
    setPhoneValue(newValue || '');
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModalClose}
      shouldCloseOnOverlayClick={false}
      style={{
        content: {
          maxWidth: '320px',
          minWidth: '296px',
          height: 'fit-content',
          borderRadius: '10px',
          padding: '25px 10px',
          inset: '0',
          margin: 'auto',
          boxShadow: '0px 2px 6px 0px rgba(134, 92, 208, 0.50)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Poppins',
          gap: '12px',
        },
        overlay: {
          background: theme.colors.modalBackground,
        },
      }}
    >
      <LoadingSplash
        isVisible={isSubmitting}
        text={`Editing Booking of Table ${seatInformation.serial_no}...`}
        spinnerColor={theme.colors.primary}
        backgroundColor="rgba(255, 255, 255, 0.95)"
        textColor={theme.colors.primary}
        spinnerSize="40px"
        borderRadius="10px"
      />
      <StyledModalTitleWrapper>
        <StyledModalHeader>Table: {seatInformation.serial_no}</StyledModalHeader>
        <ModalCloseButton onClose={handleModalClose} />
      </StyledModalTitleWrapper>

      <StyledModalDetailsWrapper>
        <label>
          Booked For <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <StyledModalDetails>
          {seatInformation.reservation_details?.booked_for || '-'}
        </StyledModalDetails>
      </StyledModalDetailsWrapper>

      <StyledModalDetailsWrapper>
        <label>Phone Number</label>
        <TableBookingDetailPhoneInputWrapper $disabled={true}>
          <PhoneInput
            onChange={handlePhoneChange}
            defaultCountry="NP"
            disabled={true}
            international={false}
            value={phoneValue}
            placeholder="-"
          />
        </TableBookingDetailPhoneInputWrapper>
      </StyledModalDetailsWrapper>

      <StyledModalDetailsWrapper>
        <label>Email</label>
        <StyledModalDetails>{seatInformation.reservation_details?.email || '-'}</StyledModalDetails>
      </StyledModalDetailsWrapper>

      <StyledModalDetailsWrapper>
        <StyledInputLabel>Remarks</StyledInputLabel>
        <StyledTextAreaField
          value={editFormData.remarks}
          onChange={(e) =>
            setEditFormData((prev) => ({
              ...prev,
              remarks: e.target.value,
            }))
          }
          placeholder="Enter remarks..."
        />
      </StyledModalDetailsWrapper>

      <StyledBottomButtonWrapper>
        <StyledFilledBorderButton onClick={handleModalClose} disabled={isLoading}>
          Cancel
        </StyledFilledBorderButton>
        <StyledSavedButton onClick={handleEditSeatAction} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </StyledSavedButton>
      </StyledBottomButtonWrapper>
    </Modal>
  );
};

export default EditBookingModal;
